import axios from 'axios';

const axiosInstance = axios.create({
  baseURL: '/api/proxy/',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a response interceptor to directly return data
axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // You can handle errors globally here if needed, or just re-throw
    return Promise.reject(error);
  }, // Added trailing comma
);

// Matches backend SubscriptionType enum
export type SubscriptionType = 'TRIAL' | 'STARTER' | 'PRO' | 'MAX' | 'ESSENTIAL' | 'ENTERPRISE' | 'NONE';
export type PlanType = 'YEARLY'; // Yearly only

export interface SubscriptionDetails {
  type: SubscriptionType;
  subscriptionSource?: string | null; // 'PAID' | 'COUPON' | 'TRIAL'
  startedAt?: string | null;
  isSubscriptionActive: boolean;
  clientLimit?: number;
  clientCount?: number;
  canAddClients?: boolean;
  daysRemaining?: number;
  expiryDate?: string;
  status?: string; // 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'PAUSED'
}

export interface SubscriptionStatusResponse {
  status: 'success' | 'error';
  message?: string;
  subscription: SubscriptionDetails;
  error?: string;
}

export interface EnableTrialResponse {
  status: 'success' | 'error';
  message: string;
  subscription?: SubscriptionDetails;
  error?: string;
}

export interface SelectSubscriptionPayload {
  subscriptionType: 'TRIAL' | 'STARTER' | 'PRO' | 'MAX';
}

export interface SelectSubscriptionResponse {
  status: 'success' | 'error';
  message: string;
  subscription?: SubscriptionDetails;
  error?: string;
}

export interface PlanPricing {
  price: number;
  finalPrice: number;
  clientLimit: number;
}

export interface PricingInfo {
  starter: {
    yearly: PlanPricing;
  };
  pro: {
    yearly: PlanPricing;
  };
  max: {
    yearly: PlanPricing;
  };
  trial: {
    durationDays: number;
    clientLimit: number;
  };
}

export interface GetPricingResponse {
  status: 'success' | 'error';
  pricing?: PricingInfo;
  error?: string;
}

export interface UpgradeSubscriptionPayload {
  subscriptionType: 'STARTER' | 'PRO' | 'MAX';
  billingInfo: BillingInfo;
}

export interface RazorpayOrder {
  id: string;
  amount: number;
  currency: string;
  key: string;
}

export interface Pricing {
  baseAmount: number;
  discountPercent: number;
  discountAmount: number;
  finalAmount: number;
  recurring: boolean;
  interval: 'monthly' | 'yearly' | string;
}

export interface UpgradeSubscriptionResponse {
  status: 'success' | 'error';
  message: string;
  isOrderPayment?: boolean;
  order?: RazorpayOrder;
  subscription?: {
    type: 'STARTER' | 'PRO' | 'MAX';
    clientLimit: number;
    nextBillingDate: string;
  };
  pricing: Pricing;
  user: {
    name: string;
    phone: string;
    panId: string;
  };
  error?: string;
}

export interface TrialStatusResponse {
  status: 'success' | 'error';
  message: string;
  isTrialExpired: boolean;
  error?: string;
}

export interface UnsubscribeResponse {
  status: 'success' | 'error';
  message: string;
  subscription?: {
    type: SubscriptionType | null;
    isSubscriptionActive: boolean;
    isCancelled: boolean;
    subscriptionEnd: string | null;
    remainingDays: number;
    clientLimit: number;
  };
  error?: string;
}

export interface RenewSubscriptionResponse {
  status: 'success' | 'error';
  message: string;
  order?: {
    id: string;
    amount: number;
    currency: string;
    key: string;
  };
  subscription?: {
    type: SubscriptionType | null;
    expiresAt: string | null;
    isSubscriptionActive: boolean;
    isCancelled: boolean;
    subscriptionEnd: string | null;
    remainingDays: number;
    clientLimit: number;
    clientCount?: number;
    status?: string;
  };
  pricing?: {
    baseAmount: number;
    discountPercent: number;
    discountAmount: number;
    finalAmount: number;
    recurring: boolean;
    interval: 'monthly' | 'yearly' | string;
  };
  error?: string;
}

export interface BillingInfo {
  organizationName: string;
  panId: string;
  phone: string;
  address: string;
  gstin?: string;
  isGstVerified?: boolean;
}

export interface RenewSubscriptionPayload {
  subscriptionType: 'STARTER' | 'PRO' | 'MAX';
  billingInfo?: BillingInfo;
}

export interface ValidateCouponResponse {
  status: 'success' | 'error';
  coupon: {
    code: string;
    description: string | null;
    durationDays: number;
    isValid: boolean;
    message?: string;
  };
}

export interface RedeemCouponPayload {
  couponCode: string;
  subscriptionType: 'STARTER' | 'PRO' | 'MAX';
}

export interface RedeemCouponResponse {
  status: 'success' | 'error';
  message: string;
  token?: string;
  subscription?: {
    type: SubscriptionType;
    subscriptionSource: string;
    expiresAt: string;
    clientLimit: number;
  };
}

export interface UpgradePricingInfo {
  currentTier: string;
  targetTier: string;
  currentYearlyPrice: number;
  newYearlyPrice: number;
  daysRemaining: number;
  unusedCredit: number;
  upgradeCost: number;
}

export interface UpgradePricingResponse {
  status: 'success' | 'error';
  upgrade: UpgradePricingInfo | null;
  message?: string;
}

export interface VerifyPanResponse {
  status: 'success' | 'error';
  message: string;
  isGstRegistered: boolean;
  billingDetails?: {
    billingName: string;
    gstin: string;
    billingAddress: string;
    isGstVerified: boolean;
    billingPanId: string;
  };
  error?: string;
}

export interface VerifyPanPayload {
  billingPanId: string;
  stateCode: string;
}

export interface AdditionalClientsPayload {
  numberOfClients: number;
  billingName: string;
  billingPanId: string;
  billingPhone: string;
  billingAddress: string;
  isGstVerified?: boolean;
  gstin?: string;
}

export interface AdditionalClientsResponse {
  status: 'success' | 'error';
  data?: {
    checkoutType: 'subscription' | 'order';
    subscriptionId?: string;
    orderId?: string;
    key: string;
    numberOfClients: number;
    pricePerClient: number;
    daysRemaining: number;
    proratedAmount: number;
    annualRenewalAmount: number;
  };
  error?: string;
}

export const subscriptionService = {
  enableTrial: async (): Promise<EnableTrialResponse> => {
    return await axiosInstance.post('licensing/subscription/enable-trial');
  },

  getStatus: async (): Promise<SubscriptionStatusResponse> => {
    return await axiosInstance.get('licensing/subscription/status');
  },

  selectSubscription: async (
    payload: SelectSubscriptionPayload,
  ): Promise<SelectSubscriptionResponse> => {
    return await axiosInstance.post('licensing/subscription/select', payload);
  },

  getPricing: async (): Promise<GetPricingResponse> => {
    return await axiosInstance.get('licensing/subscription/pricing');
  },

  verifyPan: async (payload: VerifyPanPayload): Promise<VerifyPanResponse> => {
    return await axiosInstance.post('licensing/subscription/verify-gst', payload);
  },

  upgradeSubscription: async (
    payload: UpgradeSubscriptionPayload,
  ): Promise<UpgradeSubscriptionResponse> => {
    return await axiosInstance.post('licensing/subscription/upgrade', payload);
  },

  checkTrial: async (): Promise<TrialStatusResponse> => {
    return await axiosInstance.get('licensing/subscription/trial-status');
  },

  unsubscribe: async (): Promise<UnsubscribeResponse> => {
    return await axiosInstance.post('licensing/subscription/unsubscribe');
  },

  renewSubscription: async (
    payload?: RenewSubscriptionPayload,
  ): Promise<RenewSubscriptionResponse> => {
    return await axiosInstance.post('licensing/subscription/renew', payload || {});
  },

  validateCoupon: async (couponCode: string): Promise<ValidateCouponResponse> => {
    return await axiosInstance.post('licensing/subscription/validate-coupon', { couponCode });
  },

  redeemCoupon: async (payload: RedeemCouponPayload): Promise<RedeemCouponResponse> => {
    return await axiosInstance.post('licensing/subscription/redeem-coupon', payload);
  },

  getUpgradePricing: async (targetTier: string): Promise<UpgradePricingResponse> => {
    return await axiosInstance.get(`licensing/subscription/upgrade-pricing?targetTier=${targetTier}`);
  },

  purchaseAdditionalClients: async (
    payload: AdditionalClientsPayload,
  ): Promise<AdditionalClientsResponse> => {
    return await axiosInstance.post('pay/payment/additional-clients/order', payload);
  },
};
